<template>
  <div class="main">
    <FormWrapper :action="formAction" :method="formMethod">
      <div class="title-bar">
        <h1>{{ !!company.id ? "Edit" : "Add New" }} Company</h1>
        <a class="btn-back" :href="route('admin.companies.index')">
          <span class="icon icon-arrow-left"></span>
          <span class="text">Back to List</span>
        </a>
      </div>

      <div class="row no-gutters py-4">
        <div class="col-12 col-lg-8 col-xl-9">
          <div class="row no-gutters px-3 pl-lg-4 pr-lg-0 pl-xl-5">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" />
              </transition>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Company Details"
                target="toggleGroupDetails"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupDetails" class="collapse show row">
                <div class="col-12 col-md-6 col-xl-8 mb-3">
                  <Field
                    type="text"
                    name="name"
                    label="Company Name *"
                    :value="company.name"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-4 mb-3">
                  <Field
                    type="select"
                    name="type"
                    v-model="selectedCompanyType"
                    label="Company Type *"
                    :options="types"
                    :hide-select-empty-option="true"
                  />
                </div>
                <div class="col-12 col-md-6 mb-3">
                  <Field
                    type="text"
                    name="address1"
                    label="Address Line 1"
                    :value="company.address1"
                  />
                </div>
                <div class="col-12 col-md-6 mb-3">
                  <Field
                    type="text"
                    name="address2"
                    label="Address Line 2"
                    :value="company.address2"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="city"
                    label="City"
                    :value="company.city"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-3 mb-3">
                  <StateField
                    name="state"
                    label="State/Province"
                    :value="company.state"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="zip"
                    label="Zip/Postal Code"
                    :value="company.zip"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-3 mb-3">
                  <CountryField
                    name="country"
                    label="Country"
                    :value="company.country"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="phone"
                    label="Phone Number"
                    :value="company.phone"
                  />
                </div>
                <div class="col-12 col-md-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="fax"
                    label="Fax Number"
                    :value="company.fax"
                  />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Company Brands"
                target="toggleGroupBrands"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupBrands" class="collapse show row">
                <div class="col-12 col-sm-6 col-md-4 col-xl-3 mb-3">
                  <Field
                    type="select"
                    name="brand_select"
                    v-model="selectedBrand"
                    :options="companyBrands"
                    :hide-select-empty-option="true"
                  />
                </div>
                <div class="col-12 col-sm-6 col-md-4 col-xl-3 mb-3">
                  <Field
                    type="select"
                    name="distributor_select"
                    v-model="selectedDistributor"
                    :options="distributors"
                  />
                </div>
                <div
                  class="col-12 col-sm-6 col-md-4 col-xl-6 mb-3 text-center text-sm-left"
                >
                  <a
                    class="btn btn-info"
                    href="#"
                    @click.prevent="assignBrand()"
                    >Assign Brand</a
                  >
                </div>
                <div
                  v-if="assignedBrands.length > 0"
                  class="col-12 multi-assign-values"
                >
                  <a
                    v-for="(item, i) in assignedBrands"
                    :key="i"
                    class="pill"
                    href="#"
                    @click.prevent="removeBrand(i)"
                  >
                    <span class="text">{{
                      `${item.brand_name} - ${item.parent_name}`
                    }}</span>
                    <icon icon="remove" class="icon" />
                    <input
                      type="hidden"
                      :name="`company_brands[${i}][brand_id]`"
                      :value="item.brand_id"
                    />
                    <input
                      type="hidden"
                      :name="`company_brands[${i}][parent_id]`"
                      :value="item.parent_id"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
          <div class="sticky-top">
            <div class="row no-gutters text-center">
              <div class="col-12">
                <h4 class="text-uppercase text-center">Status</h4>
                <SwitchField
                  name="is_active"
                  on-label="Active"
                  off-label="Inactive"
                  :value="!!company.is_active ? 1 : 0"
                />
                <hr class="w-75" />
              </div>
              <div class="col-12 pb-3">
                <button type="submit" class="btn btn-success w-50">
                  Save Changes
                </button>
              </div>
              <div class="col-12 pb-3">
                <a
                  :href="route('admin.courses.index')"
                  class="btn btn-danger w-50"
                  >Cancel & Exit</a
                >
              </div>
              <hr v-if="company.id" class="w-75 my-3" />
              <div v-if="company.id" class="col-12 py-2">
                <a
                  :href="
                    route('admin.companies.history.index', {
                      company: company.id,
                      type: 'online',
                    })
                  "
                  class="btn btn-secondary w-50"
                  >Training History</a
                >
              </div>
              <div v-if="company.id" class="col-12 py-2">
                <a
                  href="#"
                  @click.prevent="showHoursModal()"
                  class="btn btn-secondary w-50"
                  >Hours</a
                >
              </div>
              <template v-if="!!company.id">
                <div class="col-12 font-size-sm text-center pt-4">
                  <span
                    >Last updated
                    {{ company.updated_at | moment("M/D/YYYY h:mma") }}</span
                  >
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";

export default {
  mixins: [DisplaysAlerts],

  props: {
    company: { type: Object, required: true },
    types: { type: Array, required: true },
    brands: { type: Array, required: true },
  },

  data() {
    return {
      distributors: [],
      selectedBrand: null,
      selectedDistributor: null,
      selectedCompanyType: null,
      assignedBrands: [],
    };
  },

  computed: {
    formAction() {
      return !!this.company.id
        ? this.route("admin.companies.update", { company: this.company.id })
        : this.route("admin.companies.store");
    },

    formMethod() {
      return !!this.company.id ? "PUT" : "POST";
    },


    companyBrands() {
        if (this.selectedCompanyType === 'PUBLIC') {
            return this.brands.filter(brand => brand.label === 'MLC Public');
        }

        return this.brands;
    }
  },

  watch: {
    selectedBrand(val) {
      this.fetchDistributors();
    },
  },

  methods: {
    fetchDistributors() {
      this.axios
        .get(this.route("admin.companies.list"), {
          params: {
            ...{ "filters[brand]": [this.selectedBrand] },
            ...{ "filters[is_le]": [1] },
            ...{ "filters[is_active]": [1] },
            ...{ "pagination[current]": 1 },
            ...{ "pagination[per_page]": 1000 },
            ...{ "sort[column]": "name" },
            ...{ "sort[dir]": "asc" },
            forget: true,
          },
        })
        .then((response) => {
            this.distributors = [];
            response.data.results.forEach((item) => {
                this.distributors.push({ label: `${item.name} (${item.hvac_id})`, value: item.id });
            });
        })
        .catch((error) => {
          console.log(error);
        });
    },

    assignBrand() {
      if (!this.selectedBrand) {
        alert("Please select a brand");
      } else if (this.brandIsAssigned()) {
        alert("You have already assigned this brand");
      } else {
        this.assignedBrands.push({
          brand_id: this.selectedBrand,
          parent_id: this.selectedDistributor,
          brand_name: this.brandName(this.selectedBrand),
          parent_name: this.distributorName(this.selectedDistributor),
        });
      }
    },

    removeBrand(index) {
        this.assignedBrands.splice(index);
    },

    brandIsAssigned() {
      for (var i = 0; i < this.assignedBrands.length; i++) {
        if (this.assignedBrands[i].brand_id == this.selectedBrand) {
          return true;
        }
      }
      return false;
    },

    brandName(brandId) {
      return this.brands.find((b) => {
        return b.value == brandId;
      }).label;
    },

    distributorName(distributorId) {
      if (this.distributors.length == 0) {
        return "N/A";
      }
      return (
        this.distributors.find((d) => {
          return d.value == distributorId;
        }).label || "N/A"
      );
    },

    showHoursModal() {
      this.$modal("HoursModal", { company: this.company })
        .then(() => {})
        .catch(() => {});
    },
  },

  created() {
    this.assignedBrands = this.company.company_brands || [];
    this.selectedCompanyType = this.company.type;
  },
};
</script>